import { CHARACTERS } from "./characters.js";
/**
 * The FILE NAME of an ability's medallion. One rule, for every surface that
 * draws one.
 *
 * ── Why it lives in the protocol ──────────────────────────────────────────
 *
 * `apps/game/src/ui/abilityArt.js` made this argument one level down: the touch
 * cluster and the desktop ability row are different components, and they were
 * never going to stay in step while each computed its own filename — the icons
 * shipped to touch only and desktop kept text glyphs for a full release.
 *
 * The same thing then happened one level up. The shell's character select drew
 * the same medallions and grew its own copy of the rule, in a different app, in
 * a different language. Two apps drift for exactly the reason two components
 * do, and slower, because nothing builds them together.
 *
 * So the rule belongs to whoever owns its inputs, and that is this package:
 * `character.id` and `ability.id` are both defined here.
 *
 * ── Name, not URL ─────────────────────────────────────────────────────────
 *
 * The protocol has no opinion about where a client serves files from. It says
 * what the file is CALLED; each app adds its own base and extension. That is
 * also why this returns null rather than a placeholder path — "there is no art
 * for this slot" is a fact about the roster, and what to draw instead is a
 * decision about a screen.
 *
 * ── Keyed on `ability.id`, never on `ability.name` ────────────────────────
 *
 * characters.ts is explicit that ids are the permanent layer and names are the
 * re-skinnable theme. The first cut of these icons was named from the name
 * while the code looked up the id: all fifteen resolved to nothing, and nothing
 * failed, because nginx answers a missing file with 200 text/html.
 * `abilityArt.test.ts` is what keeps the two agreeing now.
 */
/** Slots whose art every character shares rather than owning per-kit. */
const SHARED_ART = {
    basic: "shared_strike",
    dash: "shared_dash",
    jump: "shared_jump",
};
/**
 * `slot` is the control's slot, not necessarily an ability slot: `dash` and
 * `jump` are things a player presses but have no `AbilityDef`, which is why the
 * shared map is consulted before the kit rather than after.
 */
export function abilityArtName(characterId, slot) {
    const shared = SHARED_ART[slot];
    if (shared !== undefined)
        return shared;
    const def = CHARACTERS[characterId];
    if (def === undefined)
        return null;
    const ability = def.abilities[slot];
    if (ability === undefined)
        return null;
    return `${characterId}_${ability.id}`;
}
/** Every medallion the roster requires, for a caller that needs the whole set. */
export function allAbilityArtNames() {
    const names = new Set(Object.values(SHARED_ART));
    for (const id of Object.keys(CHARACTERS)) {
        for (const slot of ["skill1", "skill2", "ultimate"]) {
            const name = abilityArtName(id, slot);
            if (name !== null)
                names.add(name);
        }
    }
    return [...names];
}
//# sourceMappingURL=abilityArt.js.map